import * as React from "react";
import {observer} from "mobx-react";
import {autorun, configure} from "mobx"; configure({ enforceActions: "never" });

import "./app.scss";

import {AppConfiguration} from "../../core/config/app-configuration";
import {ModuleConfiguration} from "../../core/config/module-configuration";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";

import {ErrorBoundary} from "../../core/components/error-boundary/error-boundary";
import esriConfig from "@arcgis/core/config";

import {MapState} from "../../core/map-state";
import {UiState} from "../../core/ui-state";
import {AppState} from "./app-mobx";

import {Sketch} from "../../core/components/sketch/sketch";
import {Footer} from "./footer/footer";
import Maintenance from "../../core/components/maintenance/maintenance";
import {GeoProxy} from "../../core/geo-proxy";

import MessageBlock from "../../core/components/message-block/message-block";
import EsriCompass from "@arcgis/core/widgets/Compass";

import {FeatureHighlight} from "./map/features/feature-highlight";
import {FeatureSelection} from "./map/features/feature-selection";

import {Api} from "./api";

import {WebMap} from "../../core/geo";
import {Search} from "../../core/components/search/search";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import {CSSProperties} from "react";

import {MapViewerStore} from "../../stores/map-viewer-store-mobx";
import {AppStoreProvider} from "../../core/stores/abstract-app-store";

configure({ enforceActions: "never" });

export interface AppProps {
    config: AppConfiguration;
    moduleConfig: ModuleConfiguration;
    appStore: AppState;
}

/**
 * Main App class containing the map
 */
@observer
export class App extends React.Component<AppProps, any> {
    mapViewerStore: MapViewerStore;

    autorunDisposers: any[] = [];
    appStore: AppState;
    uiStore: UiState;
    mapStore: MapState;

    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;

    appUiRef = React.createRef<HTMLDivElement>();
    api: Api;

    defaultScale?: number;

    constructor(props: AppProps) {
        super(props);
        this.appStore = this.props.appStore;
        this.uiStore = new UiState();
        this.mapStore = new MapState();
        this.state = {
            northFacing: true,
            renderFlag: true
        };

        this.uiStore.setUiSize(window.innerWidth, window.innerHeight);

        this.mapViewerConfig = this.props.moduleConfig as MapViewerConfiguration;
        this.appConfig = props.config as AppConfiguration;
        this.defaultScale = this.appConfig.mapView.scale;

        // initialize the core application store
        this.mapViewerStore = new MapViewerStore({
            appConfig: this.appConfig,
            mapViewerConfig: this.mapViewerConfig,
            appStore: this.appStore,
            mapStore: this.mapStore,
            uiStore: this.uiStore
        });

        // configure proxy page
        const geoProxy = new GeoProxy(this.appConfig.proxy);
        geoProxy.loadRules();
        esriConfig.request.proxyUrl = this.appConfig.proxy.url;

        // configure assets path
        if (this.appConfig.assetsPath) {
            esriConfig.assetsPath = this.appConfig.assetsPath;
        }
        // configure fonts URL
        if (this.appConfig.fontsUrl) {
            esriConfig.fontsUrl = this.appConfig.fontsUrl;
        }
        // configure GeometryService URL
        if (this.appConfig.geometryServiceUrl) {
            esriConfig.geometryServiceUrl = this.appConfig.geometryServiceUrl;
        }
        // configure portal
        if (this.appConfig.portalUrl) {
            esriConfig.portalUrl = this.appConfig.portalUrl;
            // Esri Anmeldedialog unterdrücken
            // eslint-disable-next-line max-len
            // https://community.esri.com/t5/arcgis-api-for-javascript-questions/detect-or-prevent-the-arcgis-authentication-dialog/td-p/1047565
            IdentityManager.on("dialog-create", () => {
                // eslint-disable-next-line no-console
                console.warn("Ungenügende Berechtigung (IdentityManager dialog-create)");
                IdentityManager.dialog.destroy();
            });
        } else {
            // eslint-disable-next-line no-console
            console.warn("No portal connection defined.");
        }
        this.mapStore.setAppConfig(this.appConfig);

        this.handleMapLoad = this.handleMapLoad.bind(this);
        this.handleUnmount = this.handleUnmount.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.goToDefaultExtent = this.goToDefaultExtent.bind(this);

        this.api = new Api(this.props.appStore, this.mapStore, this.handleUnmount, this.goToDefaultExtent);
    }
    handleUnmount(renderFlag: boolean | void): void {
        if (typeof renderFlag === "boolean"){
            this.setState({renderFlag});
        } else {
            this.setState({renderFlag: false});
        }
        if (!this.state.renderFlag) {
            this.props.appStore.setMode("sketch");
            this.props.appStore.setVisibleLayers([]);
        }
        this.forceUpdate();
    }
    handleResize(): void {
        this.checkUiSize();
    }
    checkUiSize(): void {
        const uiHeight = this.appUiRef.current?.clientHeight;
        const uiWidth = this.appUiRef.current?.clientWidth;
        const isSmallDevice = (window.innerWidth <= this.mapViewerConfig.breakpoints.md);
        this.uiStore.setUiSize(uiWidth, uiHeight, isSmallDevice);
    }
    goToDefaultExtent(): void {
        this.mapStore.view?.goTo({
            target: (this.appConfig.mapView.center as number[]),
            scale: this.defaultScale} as __esri.GoToTarget2D).catch((error) => {
            if (error.name !== "view:goto-interrupted") {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        });
    }
    componentDidMount(): void {
        this.mapStore.setWebMap(this.mapViewerConfig.webMap);
        autorun((reaction: any) => {
            const view = this.mapStore.view;
            if (!view) {
                return;
            }
            this.checkUiSize();
            reaction.dispose(); // stop autorun
        });
        this.checkUiSize();
        window.addEventListener("resize", this.handleResize);
    }
    componentWillUnmount(): void {
        window.removeEventListener("resize", this.handleResize);
    }
    render(): JSX.Element | null {
        let mapProperties: __esri.WebMapProperties | undefined;
        const messages: JSX.Element[] = [];
        const {maintenance} = this.appConfig;
        const {uiHeight} = this.uiStore;
        const {northFacing, renderFlag} = this.state;
        const {status, webMap} = this.mapStore;
        const {appStore} = this.props;
        if (!renderFlag) {
            return null;
        }
        if (webMap){
            mapProperties = webMap as __esri.WebMapProperties;
            if (this.mapViewerConfig.meta && Array.isArray(this.mapViewerConfig.meta.alerts)) {
                this.mapViewerConfig.meta.alerts.forEach((message, index) => {
                    // quick and dirty!
                    const alertContent = <p dangerouslySetInnerHTML={{__html: message.content}} />;
                    messages.push(
                        <MessageBlock
                            index={index}
                            topic={"datenshop"}
                            header={message.title}
                            className={message.class? message.class: undefined}
                            content={alertContent}
                            appConfig={this.appConfig}/>
                    );
                });
            }
        }
        const cssProperties = { "--uiHeight": uiHeight + "px" } as CSSProperties;
        let addClass = "geo-app-body " + status;
        addClass += (northFacing? " geo-north-facing": " geo-not-north-facing");
        addClass += (appStore.maxObject===1? " geo-sketch-only_one": "");
        return(
            <ErrorBoundary>
                <AppStoreProvider store={this.mapViewerStore}>
                <div className={addClass} style={cssProperties}>

                    <WebMap
                        mapProperties={mapProperties}
                        viewProperties={this.appConfig.mapView as __esri.MapViewProperties}
                        onLoad={this.handleMapLoad}
                        store={this.mapStore}
                        uiStore={this.uiStore}>
                    </WebMap>
                    <Sketch
                        config={this.appConfig.sketch}
                        view={this.mapStore.view}
                        />
                    <FeatureSelection
                        symbols={this.mapViewerConfig.symbols} />
                    <FeatureHighlight
                        appConfig={this.appConfig}
                        symbols={this.mapViewerConfig.symbols} />
                    <div className={"geo-app"} ref={this.appUiRef}>
                        <Maintenance config={maintenance} uiStore={this.uiStore} />
                        <div className={"geo-ui-root"}>
                            <div className={"geo-ui"}>
                                <div className={"geo-ui-inner-container geo-ui-corner-container"}>
                                    <div className={"geo-ui-top-left geo-ui-corner"} />
                                    <div className={"geo-ui-top-right geo-ui-corner"}>
                                        <Search
                                            key={"search"}
                                            mapStore={this.mapStore}
                                            uiStore={this.uiStore}
                                            config={this.appConfig.search}
                                        />
                                    </div>
                                    <div className={"geo-ui-bottom-left geo-ui-corner"}>
                                        { messages.length > 0 && <div className="geo-messages">{messages}</div> }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Footer view={this.mapStore.view}
                            appStore={appStore}
                            appConfig={this.appConfig}
                            uiStore={this.uiStore}
                            mapStore={this.mapStore}
                            mapViewerConfig={this.mapViewerConfig}/>

                    </div>
                </div>
                </AppStoreProvider>
            </ErrorBoundary>
        );
    }

    handleMapLoad(map: __esri.Map, view: __esri.MapView | __esri.SceneView): void {
        const compassWidget = new EsriCompass({
            view
        });
        view.ui.add(compassWidget, "bottom-left");
        view.watch("rotation", (newValue: number)=>{
            this.setState({northFacing: newValue === 0});
        });
        view.ui.move("zoom", "bottom-left");
        view.popupEnabled = false;
        view.closePopup();
    }
}
